import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import { useClients } from '../clients/useData';

export interface ClientInvoice {
  invoiceId: number;
  clientId: number;
  invoiceType: number;
  InvoiceCategoryId?: number;
  InvoiceDate?: string;
  invoiceDue?: string;
  InvoiceNo?: string;
  invoiceDescription?: string;
  invoiceCurrency?: number;
  invoiceAmount?: number;
  invoiceDiscount?: number;
  invoiceVAT?: number;
  invoiceIncome?: number;
  invoiceNotes?: string;
  supportAgreementId?: number;
  invoiceCreatedBy?: number;
  invoiceUpdatedBy?: number;
  paymentTotal?: number | null;
  // Related data
  client?: {
    clientName?: string;
  };
  invoiceTypeDescr?: {
    pit_descr?: string;
    pit_abbrev?: string | null;
  };
  invoiceCategoryDescr?: {
    pic_descr?: string;
  };
  supportAgreement?: {
    id: number;
    description?: string;
    agreementType?: string;
  };
  FileAttachment?: Array<{
    id: number;
    fileName: string;
    filePath: string;
    fileType: string;
    fileSize: number;
    uploadedAt?: string;
  }>;
  paymentReminders?: Array<{
    id: number;
    contactedAt: string;
    responseStatus?: string | null;
    responseNote?: string | null;
    responseAt?: string | null;
  }>;
  // Computed fields
  status?: string;
  overdueDays?: number;
}

export interface ClientInvoicesResponse {
  records: ClientInvoice[];
  paidCount: number;
  partialCount: number;
  unpaidCount: number;
  overdueCount: number;
}

export const useClientInvoices = (searchParams?: string, filter?: string) => {
  let queryString = '';
  if (searchParams || filter) {
    const params = new URLSearchParams();
    if (searchParams) params.append('searchParams', searchParams);
    if (filter) params.append('filter', filter);
    queryString = `?${params.toString()}`;
  }

  const { data, error, isLoading, mutate } = useSWR<ClientInvoicesResponse>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  const invoices = data?.records || [];

  return {
    data: invoices,
    invoices: invoices,
    total: invoices.length,
    isLoading,
    isError: error,
    mutate,
    paidCount: data?.paidCount || 0,
    partialCount: data?.partialCount || 0,
    unpaidCount: data?.unpaidCount || 0,
    overdueCount: data?.overdueCount || 0,
  };
};

export const useClientInvoice = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<ClientInvoice>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    invoice: data,
    data: data,
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get clients for select dropdown
export const useClientsForSelect = () => {
  const { clients, isLoading } = useClients();

  return {
    clients: clients || [],
    isLoading,
    options: (clients || []).map((client) => ({
      value: String(client.clientId),
      label: client.clientName || `Client #${client.clientId}`,
    })),
  };
};

// Hook to get invoice types for select dropdown
export const useInvoiceTypesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-crm-invoice-type',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    invoiceTypes: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((type) => ({
      value: String(type.id),
      label: type.pit_abbrev || type.pit_descr || `Invoice Type #${type.id}`,
    })),
  };
};

// Hook to get invoice categories for select dropdown
export const useInvoiceCategoriesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-crm-invoice-category',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    invoiceCategories: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((category) => ({
      value: String(category.id),
      label: category.pic_descr || `Category #${category.id}`,
    })),
  };
};

// Hook to get support agreements for select dropdown (filtered by clientId)
export const useSupportAgreementsForSelect = (clientId?: number) => {
  const { data, error, isLoading } = useSWR<any>(
    clientId ? '/crm-support-agreements' : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const agreements = clientId && data?.records
    ? data.records.filter((agreement: any) => agreement.clientId === clientId)
    : [];

  return {
    supportAgreements: agreements,
    isLoading,
    isError: error,
    options: agreements.map((agreement: any) => ({
      value: String(agreement.id),
      label: agreement.description || `${agreement.agreementType || ''} - #${agreement.id}`.trim() || `Agreement #${agreement.id}`,
    })),
  };
};

